import { useMemo } from 'react';

import { useLocale } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import { useTriggerContext } from '@/components/providers/trigger-context-provider';
import { Locale } from '@/i18n/locales';
import { getLocalizedValue } from '@/lib/utils/localization';

function findTriggerOverrideKey(
  triggerOverrides: Record<string, unknown>,
  trigger: string,
): string | undefined {
  return Object.keys(triggerOverrides)
    .filter(key => trigger.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
}

interface LocalizedConfigWithSource<T> {
  value: T | null;
  isFromTriggerOverride: boolean;
}

/**
 * The localized config value, plus whether a matching trigger override supplied it instead of the
 * tenant's own translations.
 */
export function useLocalizedConfigWithSource<T = unknown>(
  keyPath: string,
): LocalizedConfigWithSource<T> {
  const config = useConfig();
  const locale = useLocale() as Locale;
  const trigger = useTriggerContext();

  return useMemo(() => {
    if (trigger && config.triggerOverrides) {
      const overrideKey = findTriggerOverrideKey(config.triggerOverrides, trigger);
      if (overrideKey) {
        const override = config.triggerOverrides[overrideKey];
        const overrideValue = getLocalizedValue<T>(
          { ...config, translations: override.translations },
          locale,
          keyPath,
        );
        if (overrideValue !== null) return { value: overrideValue, isFromTriggerOverride: true };
      }
    }
    return { value: getLocalizedValue<T>(config, locale, keyPath), isFromTriggerOverride: false };
  }, [config, locale, keyPath, trigger]);
}

/** The localized config value; a matching trigger override wins over the tenant's translations. */
export function useLocalizedConfig<T = unknown>(keyPath: string): T | null {
  return useLocalizedConfigWithSource<T>(keyPath).value;
}
